import { DatePicker, DatePickerProps } from "@progress/kendo-react-dateinputs";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint } from "@progress/kendo-react-labels";
import * as React from "react";
import FormLabel from "./FormLabel";
import { useControlledField, type FieldValidator } from "./useControlledField";

export interface FormDatePickerProps
  extends Omit<DatePickerProps, "name" | "value" | "onChange" | "valid"> {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
}

export const FormDatePicker: React.FC<FormDatePickerProps> = ({
  name,
  validator,
  label,
  hint,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  disabled,
  placeholder,
  ...datePickerProps
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const inputId = name;
  const hintId = hint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";

  /* `field.value` is typed as Date but isn't guaranteed to be one at runtime:
     a caller can seed RHF (via defaultValues or a bulk reset) with a raw API
     string before it has the metadata needed to coerce it. Kendo's DatePicker
     calls `.getTime()` on `value` during render (not in an effect), so a raw
     string here crashes synchronously — there is no later tick to recover in.
     Coerce defensively rather than trusting the type. This is presentational
     only: form state keeps the raw value, so an unparseable string renders
     as an empty picker without normalizing what would be submitted. */
  const rawValue = field.value as unknown;
  const dateValue = React.useMemo(() => {
    const coerced =
      rawValue instanceof Date
        ? rawValue
        : rawValue
          ? new Date(rawValue as string | number)
          : null;
    return coerced && !Number.isNaN(coerced.getTime()) ? coerced : null;
  }, [rawValue]);

  return (
    <FieldWrapper>
      <FormLabel
        id={label ? `${inputId}_label` : ""}
        editorId={inputId}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        // "blank" keeps the floating label unfloated when empty (Kendo Form convention).
        value={dateValue ? String(dateValue) : "blank"}
        placeholder={placeholder ?? undefined}
        optional={optional}
      >
        <DatePicker
          {...datePickerProps}
          name={name}
          disabled={disabled}
          ariaDescribedBy={`${hintId} ${errorId}`}
          aria-invalid={!!fieldState.error || undefined}
          valid={!fieldState.error}
          value={dateValue}
          onChange={e => field.onChange(e.value)}
          onBlur={field.onBlur}
        />
        {hint && <Hint id={hintId}>{hint}</Hint>}
        {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
      </FormLabel>
    </FieldWrapper>
  );
};

export default FormDatePicker;
